import React, { Children, Component, PropTypes, PureComponent } from 'react';
import _ from 'underscore';
import { Button, Icon, Popover, Tip } from 'react-fast-ui';
import ConfirmButton from './buttons/ConfirmButton';
import FormattedAddress from '../components/FormattedAddress';
import cx from 'classnames';
import OriginalTreeView from 'react-treeview';
import { Link } from 'react-router';
import { toUrlData } from '../util/UrlParams';
import DelayedOnClickOut from '../components/DelayedOnClickOut';
import { toQueryParams } from '../GameDay/components/pages/AdminGamesListPage';
import { paramify } from '../util/Etc';

class ActionsPopover extends PureComponent {
  state = {
    open: false
  };

  openPopover = () => this.setState({ open: true });
  closePopover = () => this.setState({ open: false });

  render() {
    const { children, ...rest } = this.props;
    const { open } = this.state;

    return (
      <Popover open={open} style={{ zIndex: 40 }} content={
        open ? (
          <DelayedOnClickOut onClickOut={this.closePopover}>
            <div className="mg-xs text-nowrap">{children}</div>
          </DelayedOnClickOut>
        ) : <span/>
      } {...rest}>
        <span onClick={this.openPopover} className="color-primary hover-color-primary-darken cursor-pointer mg-sm">
          <Icon name="ellipsis-h"/>
        </span>
      </Popover>
    );
  }
}


const EditButton = props => (
  <Button icon="pencil" borderless={true} size="sm" {...props}/>
);

const AddButton = props => (
  <Button icon="plus" type="success" borderless={true} size="sm" {...props}/>
);

const DeleteButton = ({ onClick, ...rest }) => (
  <ConfirmButton icon="trash" type="danger" borderless={true} size="sm" {...rest} onConfirm={onClick}/>
);

const ViewNotificationsButton = ({ onClick, ...rest }) => (
  <Button icon="commenting-o" type="primary" borderless={true} size="sm" {...rest} onClick={onClick}/>
);

const ActionButtons = ({ onEdit, onAdd, onDelete, onViewNotifications }) => (
  <span>
    {onEdit && <EditButton onClick={onEdit}/>}
    {onAdd && <AddButton onClick={onAdd}/>}
    {onViewNotifications && <ViewNotificationsButton onClick={onViewNotifications}/>}
    {onDelete && <DeleteButton onClick={onDelete}/>}
  </span>
);

export const GameStats = ({ stats: { gamesPlayed, wins, ties, pointDifferential }, ...rest }) => (
  <span>
    <span className="color-success">{wins}</span>
    /
    <span className="color-warning">{ties}</span>
    /
    <span className="color-danger">{gamesPlayed - wins - ties}</span>
    &nbsp;
    <strong className={cx({ 'color-danger': pointDifferential < 0, 'color-success': pointDifferential > 0 })}>
      {pointDifferential >= 0 ? '+' : '-'}{Math.abs(pointDifferential)}
    </strong>
  </span>
);

export const StandingInfo = ({ standing: { rank, note, stats }, ...rest }) => {
  const hasNote = note && note.length;

  const badge = (
    <span {...rest}
      className="badge bg-color-primary-darken">
      <strong>{rank}</strong> | <GameStats stats={stats}/>
    </span>
  );

  if (hasNote) {
    return (
      <Tip tip={note}>
        {badge}
      </Tip>
    );
  }

  return badge;
};

export class TourneyGameLink extends Component {
  static propTypes = {
    filter: PropTypes.object.isRequired,
    tourneyId: PropTypes.string.isRequired
  };

  shouldComponentUpdate(nextProps) {
    return !_.isEqual(nextProps, this.props);
  }

  render() {
    const { filter, tourneyId, children, ...rest } = this.props;

    return (
      <Link {...rest} target="_blank" to={`tourneys/${tourneyId}/games/?${paramify(toQueryParams(filter))}`}>
        { children || <Icon name="share"/> }
      </Link>
    );
  }
}

export const TYPES = {
  division: 'division',
  group: 'group',
  venue: 'venue',
  court: 'court',
  team: 'team'
};

const StyledTreeView = ({ itemClassName, label, actions = null, onClickLabel, collapsed, ...rest }) => {
  const canCollapse = !!onClickLabel;
  const arrow = canCollapse ? (<span className={cx({ arrow: canCollapse, collapsed })}/>) : null;

  return (
    <OriginalTreeView
      collapsed={collapsed}
      nodeLabel={
        <div className="flex-grow-1 display-flex align-items-center justify-content-center">
          <div className={cx('pd-sm flex-grow-1 display-flex align-items-center', { 'cursor-pointer': canCollapse })}
            onClick={onClickLabel}>
            {arrow}
            {label}
          </div>
          <div className="flex-shrink-0 mg-bottom-sm mg-top-sm">
            {
              actions &&
              <ActionsPopover>
                {actions}
              </ActionsPopover>
            }
          </div>
        </div>
      }
      itemClassName={cx(itemClassName, 'display-flex align-items-center')}
      {...rest}
    />
  );
};

export default class TourneyDataTree extends PureComponent {
  static propTypes = {
    expandedKeys: PropTypes.object,
    onExpandedKeysChange: PropTypes.func.isRequired,

    onViewNotifications: PropTypes.func,

    actionable: PropTypes.bool,

    onEdit: PropTypes.func.isRequired,

    onDelete: PropTypes.func.isRequired,

    onCalculateStandings: PropTypes.func.isRequired,

    tourney: PropTypes.object.isRequired,

    data: PropTypes.shape(_.mapObject(TYPES, () => PropTypes.arrayOf(PropTypes.object).isRequired)).isRequired
  };

  static defaultProps = {
    expandedIds: {},
    actionable: false,
    onViewNotifications: null
  };

  render() {
    const {
      expandedKeys,
      onExpandedKeysChange,

      onViewNotifications,

      actionable,

      tourney, onEdit, onDelete, onCalculateStandings,
      data: {
        [ TYPES.venue ]: venues,
        [ TYPES.court ]: courts,
        [ TYPES.team ]: teams,
        [ TYPES.group ]: groups,
        [ TYPES.division ]: divisions,
      }
    } = this.props;

    const Buttons = props => {
      return actionable ? <ActionButtons {...props}/> : null;
    };

    const courtsByVenueId = _.groupBy(courts, ({ venue: { id } }) => id);

    const teamsByPoolId = _.chain(teams)
        .groupBy(({ pool: { id } }) => id)
        .mapObject(teams => {
          return _.sortBy(teams, ({ standing, name }) => { return standing ? standing.rank : name; });
        })
        .value();
    const groupsByDivisionIdAndType = _.chain(groups)
        .groupBy(({ division: { id } }) => id)
        .mapObject(groups => ({ Pool: [], Bracket: [], ..._.groupBy(groups, 'type') }))
        .value();

    const GameLink = props => (
      <TourneyGameLink onClick={e => e.stopPropagation()} className="pd-sm" target="_blank"
        tourneyId={tourney.id} {...props}/>
    );

    const CalculateStandingsButton = ({ groups }) => (
      actionable ? (
        <Button
          size="sm" onClick={() => onCalculateStandings(groups)}
          borderless={true}
          icon="sort-numeric-asc"/>
      ) : null
    );

    const ActionableTreeView = ({ actions, ...rest }) => (
      <StyledTreeView {...rest} actions={actionable ? actions : null}/>
    );

    const ControlledTreeView = ({ id, ...rest }) => {
      const expanded = !id || Boolean(expandedKeys[ id ]);

      return (
        <ActionableTreeView
          {...rest}
          collapsed={!expanded}
          onClickLabel={id ? (e => onExpandedKeysChange({ ...expandedKeys, [ id ]: !expanded })) : null}
        />
      );
    };


    const viewNotificationsFn = (type, object) => {
      const handler = () => onViewNotifications(type, object);
      return onViewNotifications && handler;
    };

    return (
      <ActionableTreeView key={tourney.id} collapsed={false} label={
        <div><Icon name="trophy"/> {tourney.name}</div>
      }>
        <ActionableTreeView
          key="divisions"
          collapsed={false}
          label={<div>Divisions</div>}
          actions={actionable ? <Buttons onAdd={() => onEdit(TYPES.division, { tourney })}/> : null}>
          {
            divisions.map(
              division => (
                <ControlledTreeView
                  key={division.id} id={division.id}
                  actions={
                    <div>
                      <Buttons
                        onEdit={() => onEdit(TYPES.division, division)}
                        onViewNotifications={viewNotificationsFn(TYPES.division, division)}
                        onDelete={() => onDelete(TYPES.division, division)}
                      />
                    </div>
                  }
                  label={<div>{division.name} <GameLink filter={{ division }}/></div>}>
                  {
                    _.chain([ 'Pool', 'Bracket' ])
                      .map(
                        type => {
                          const divisionGroups = (groupsByDivisionIdAndType[ division.id ] || {})[ type ] || [];

                          return (
                            <ActionableTreeView
                              collapsed={false}
                              key={type}
                              label={<div>{type}s</div>}
                              actions={
                                <div>
                                  <Buttons onAdd={() => onEdit(TYPES.group, { division, type })}/>
                                  {
                                    type === 'Pool' ? <CalculateStandingsButton groups={divisionGroups}/> : null
                                  }
                                </div>
                              }>
                              {
                                _.map(divisionGroups, group => {
                                  if (type === 'Bracket') {
                                    return (
                                      <ActionableTreeView
                                        key={group.id}
                                        label={
                                          <div>{group.name} <GameLink filter={{ gameGroup: group, division }}/></div>
                                        }
                                        actions={
                                          <Buttons
                                            onEdit={() => onEdit(TYPES.group, group)}
                                            onDelete={() => onDelete(TYPES.group, group)}/>
                                        }
                                      />
                                    );
                                  }

                                  return (
                                    <ControlledTreeView
                                      key={group.id} id={group.id}
                                      label={
                                        <div>{group.name} <GameLink filter={{ gameGroup: group, division }}/></div>
                                      }
                                      actions={
                                        <div>
                                          <Buttons onAdd={() => onEdit(TYPES.team, { pool: group })}
                                            onEdit={() => onEdit(TYPES.group, group)}
                                            onViewNotifications={viewNotificationsFn(TYPES.group, group)}
                                            onDelete={() => onDelete(TYPES.group, group)}/>
                                          <CalculateStandingsButton groups={[ group ]}/>
                                        </div>
                                      }>
                                      {
                                        _.chain(teamsByPoolId[ group.id ])
                                          .map(
                                            team => (
                                              <ActionableTreeView
                                                collapsed={false} key={team.id}
                                                label={
                                                  <div className="flex-grow-1 display-flex align-items-center">
                                                    {
                                                      team.standing ? (
                                                        <span className="mg-right-sm flex-shrink-0">
                                                          <StandingInfo standing={team.standing}/>
                                                        </span>
                                                      ) : null
                                                    }
                                                    <em className="flex-grow-1">
                                                      {team.name} <GameLink filter={{ team }}/>
                                                    </em>
                                                  </div>
                                                }
                                                actions={
                                                  <Buttons
                                                    onEdit={() => onEdit(TYPES.team, team)}
                                                    onViewNotifications={viewNotificationsFn(TYPES.team, team)}
                                                    onDelete={() => onDelete(TYPES.team, team)}/>
                                                }
                                              />
                                            )
                                          )
                                          .value()
                                      }
                                    </ControlledTreeView>
                                  );
                                })
                              }
                            </ActionableTreeView>
                          );
                        }
                      )
                      .value()
                  }
                </ControlledTreeView>
              )
            )
          }
        </ActionableTreeView>


        <StyledTreeView
          key="venues" collapsed={false}
          actions={<Buttons onAdd={() => onEdit(TYPES.venue, { tourney })}/>}
          label={<div>Venues</div>}>
          {
            venues.map(
              venue => (
                <ControlledTreeView
                  key={venue.id} id={venue.id}
                  label={
                    <div>
                      <div>
                        {venue.name}
                        <small className="mg-left-sm">({venue.nickname})</small>
                        <GameLink filter={{ venue }}/>
                      </div>
                      {
                        venue.location && venue.location.address ? (
                          <small>
                            <em><FormattedAddress componentSeparator=", "
                              address={venue.location.address}/></em>
                          </small>
                        ) : null
                      }
                    </div>
                  }
                  actions={
                    <div>
                      <Buttons onEdit={() => onEdit(TYPES.venue, venue)}
                        onDelete={() => onDelete(TYPES.venue, venue)}
                        onAdd={() => onEdit(TYPES.court, { venue })}/>
                    </div>
                  }>
                  {
                    _.chain(courtsByVenueId[ venue.id ])
                      .map(
                        court => (
                          <ActionableTreeView
                            key={court.id}
                            label={<div>{court.name} <GameLink filter={{ court, venue: court.venue }}/></div>}
                            actions={
                              <Buttons onEdit={() => onEdit(TYPES.court, court)}
                                onDelete={() => onDelete(TYPES.court, court)}/>
                            }/>
                        )
                      )
                      .value()
                  }
                </ControlledTreeView>
              )
            )
          }
        </StyledTreeView>
      </ActionableTreeView>
    );
  };
}


// WEBPACK FOOTER //
// ./src/js/components/TourneyDataTree.jsx